import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Button, Row, Col, Form, Card, ProgressBar, Badge, Alert } from 'react-bootstrap';
// import { getClassesBySchoolId, getStudentsByClassId, getIndividualStudentResults } from '../services/APIClient';
// Assume you have getStudentsBySchoolId and can derive class list from that

const BaseScreeningRTIComparisonModal = ({ show, onHide, schoolid, getStudentsBySchoolId, getIndividualStudentResults }) => {
  // 1. Load all students for the school and derive classes
  const [allStudents, setAllStudents] = useState([]);
  const [classList, setClassList] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [studentsInClass, setStudentsInClass] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [allAssessments, setAllAssessments] = useState([]);
  const [screeningPoints, setScreeningPoints] = useState([]);
  const [selectedScreeningId, setSelectedScreeningId] = useState('');
  const [tasksForScreening, setTasksForScreening] = useState([]);
  const [selectedTaskName, setSelectedTaskName] = useState('');
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Step 1: Load all students on open
  useEffect(() => {
    if (show && schoolid) {
      setLoading(true);
      getStudentsBySchoolId(schoolid).then(students => {
        setAllStudents(students);
        // Derive class list (unique by classId)
        const classes = [];
        students.forEach(stu => {
          if (stu.class && !classes.find(c => c.id === stu.classId)) {
            classes.push({ id: stu.classId, name: stu.class.name });
          }
        });
        setClassList(classes);
        setLoading(false);
      });
    }
  }, [show, schoolid, getStudentsBySchoolId]);

  // Step 2: When class is selected, filter students
  useEffect(() => {
    if (selectedClassId) {
      setStudentsInClass(allStudents.filter(stu => String(stu.classId) === String(selectedClassId)));
      setSelectedStudentId('');
      setAllAssessments([]);
      setScreeningPoints([]);
      setSelectedScreeningId('');
      setTasksForScreening([]);
      setSelectedTaskName('');
      setComparisonData(null);
    }
  }, [selectedClassId, allStudents]);

  // Step 3: When student is selected, load their assessments
  useEffect(() => {
    if (selectedStudentId) {
      setLoading(true);
      getIndividualStudentResults(selectedStudentId).then(assessments => {
        setAllAssessments(assessments || []);
        // Step 4: Set up screening points (rti: false only)
        const screenings = (assessments || []).filter(a => !a.rti).map(a => ({
          id: a.id,
          name: a.name,
          tasks: a.tasks
        }));
        setScreeningPoints(screenings);
        setSelectedScreeningId('');
        setTasksForScreening([]);
        setSelectedTaskName('');
        setComparisonData(null);
        setLoading(false);
      });
    }
  }, [selectedStudentId, getIndividualStudentResults]);

  // Step 5: When screening is selected, list tasks that have corresponding RTI data
  useEffect(() => {
    if (selectedScreeningId) {
      const screening = screeningPoints.find(a => a.id === selectedScreeningId);
      const screeningTasks = screening?.tasks || [];
      
      // Find RTI assessments
      const rtiAssessments = allAssessments.filter(a => a.rti);
      
      // Only include tasks that have a corresponding RTI attempt
      const tasksWithRTI = screeningTasks.filter(screeningTask => {
        return rtiAssessments.some(rtiAssessment => 
          (rtiAssessment.tasks || []).some(rtiTask => rtiTask.name === screeningTask.name)
        );
      });
      
      setTasksForScreening(tasksWithRTI.map(t => t.name));
      setSelectedTaskName('');
      setComparisonData(null);
    }
  }, [selectedScreeningId, screeningPoints, allAssessments]);

  // Step 6: When task is selected, find screening and RTI attempt for that task
  useEffect(() => {
    if (selectedTaskName) {
      // Find the screening assessment object and task
      const screening = screeningPoints.find(a => a.id === selectedScreeningId);
      const screeningTask = screening?.tasks?.find(t => t.name === selectedTaskName);

      // Now, find the most recent RTI assessment with this task
      const rtiAssessments = allAssessments.filter(a => a.rti);
      let rtiTask = null, rtiAssessment = null, rtiDate = null;
      for (let a of rtiAssessments) {
        const matchTask = (a.tasks || []).find(t => t.name === selectedTaskName);
        if (matchTask) {
          // You could sort and pick first/last, here we pick the last one found
          rtiTask = matchTask;
          rtiAssessment = a;
          rtiDate = a.dateAssigned;
        }
      }

      setComparisonData({
        left: screeningTask
          ? {
              assessment: screening.name,
              date: screening.dateAssigned ? new Date(screening.dateAssigned).toLocaleDateString() : '',
              taskName: screeningTask.name,
              score: Math.round((screeningTask.score || 0) * 100),
              timeSpent: screeningTask.duration || 0,
            }
          : null,
        right: rtiTask
          ? {
              assessment: rtiAssessment?.name,
              date: rtiDate ? new Date(rtiDate).toLocaleDateString() : '',
              taskName: rtiTask.name,
              score: Math.round((rtiTask.score || 0) * 100),
              timeSpent: rtiTask.duration || 0,
            }
          : null,
      });
    }
  }, [selectedTaskName, screeningPoints, selectedScreeningId, allAssessments]);

  // Render task result with progress bar (borrowed from BaseComparisonModal)
  const renderTaskResult = (taskData, side) => (
    <Card key={`${taskData.taskName}-${side}`} className="mb-2">
      <Card.Body className="py-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>{taskData.taskName}</strong>
          <Badge bg={taskData.score >= 70 ? "success" : taskData.score >= 50 ? "warning" : "danger"}>
            {taskData.score}%
          </Badge>
        </div>
        <ProgressBar 
          now={taskData.score} 
          variant={taskData.score >= 70 ? "success" : taskData.score >= 50 ? "warning" : "danger"}
          className="mb-1"
        />
        <small className="text-muted">
          Time: {taskData.timeSpent}s | Assessment: {taskData.assessment}
        </small>
      </Card.Body>
    </Card>
  );

  // ---- RENDER ----
  return (
    <Modal show={show} onHide={onHide} size="xl">
      <style jsx>{`
        .scrollable-results::-webkit-scrollbar {
          width: 8px;
        }
        
        .scrollable-results::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        .scrollable-results::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        
        .scrollable-results::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
        
        .scrollable-results {
          scrollbar-width: thin;
          scrollbar-color: #c1c1c1 #f1f1f1;
        }
      `}</style>
      <Modal.Header closeButton>
        <Modal.Title>Standard Screening vs RTI Task Comparison</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          <small>
            Compare a student's standard screening result (left) with their RTI intervention on the same task (right).
            Only tasks with corresponding RTI data are shown.
          </small>
        </Alert>
        <Row className="mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label><strong>Select Class</strong></Form.Label>
              <Form.Select value={selectedClassId} onChange={e => setSelectedClassId(e.target.value)}>
                <option value="">Choose class...</option>
                {classList.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label><strong>Select Student</strong></Form.Label>
              <Form.Select value={selectedStudentId} onChange={e => setSelectedStudentId(e.target.value)} disabled={!selectedClassId}>
                <option value="">Choose student...</option>
                {studentsInClass.map(stu => (
                  <option key={stu.id} value={stu.id}>{stu.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label><strong>Select Screening Point</strong></Form.Label>
              <Form.Select value={selectedScreeningId} onChange={e => setSelectedScreeningId(e.target.value)} disabled={!selectedStudentId}>
                <option value="">Choose screening...</option>
                {screeningPoints.map(sp => (
                  <option key={sp.id} value={sp.id}>{sp.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label><strong>Select Task</strong></Form.Label>
              <Form.Select value={selectedTaskName} onChange={e => setSelectedTaskName(e.target.value)} disabled={!selectedScreeningId}>
                <option value="">
                  {tasksForScreening.length === 0 && selectedScreeningId ? 
                    "No tasks with RTI data..." : 
                    "Choose task..."
                  }
                </option>
                {tasksForScreening.map(taskName => (
                  <option key={taskName} value={taskName}>{taskName}</option>
                ))}
              </Form.Select>
              {selectedScreeningId && tasksForScreening.length === 0 && (
                <small className="text-muted">
                  No tasks in this screening have corresponding RTI data
                </small>
              )}
            </Form.Group>
          </Col>
        </Row>
        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading assessments...</p>
          </div>
        )}
        {comparisonData && (
          <Row>
            <Col md={6}>
              <Card>
                <Card.Header className="bg-primary text-white">
                  <h5 className="mb-0">Screening</h5>
                  {comparisonData.left?.date && <small>📅 {comparisonData.left.date}</small>}
                </Card.Header>
                <Card.Body 
                  className="p-0 scrollable-results"
                  style={{ 
                    maxHeight: '400px', 
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }}
                >
                  <div className="p-3">
                    {comparisonData.left ? (
                      renderTaskResult(comparisonData.left, 'left')
                    ) : (
                      <div className="alert alert-info mb-0">
                        No screening data for this task.
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card>
                <Card.Header className="bg-success text-white">
                  <h5 className="mb-0">RTI</h5>
                  {comparisonData.right?.date && <small>📅 {comparisonData.right.date}</small>}
                </Card.Header>
                <Card.Body 
                  className="p-0 scrollable-results"
                  style={{ 
                    maxHeight: '400px', 
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }}
                >
                  <div className="p-3">
                    {comparisonData.right ? (
                      renderTaskResult(comparisonData.right, 'right')
                    ) : (
                      <div className="alert alert-info mb-0">
                        No RTI data for this task.
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
        {comparisonData && comparisonData.left && comparisonData.right && (
          <Button variant="primary">Export Comparison</Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BaseScreeningRTIComparisonModal;